import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner, Nav, Tab, Col } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall, getWithAuthCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const emptyConfig = {
  vehicle_id: "",
  acquisition_cost: "",
  residual_value: "",
  in_service_date: "",
  useful_life_years: "",
  planned_cpk: "",
  expected_km: "",
  odometer_start: "",
  odometer_current: "",
};

// label + value formatters so any response shape renders cleanly
const fmtLabel = (k) =>
  String(k)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "number") return v.toLocaleString();
  if (!isNaN(v)) return Number(v).toLocaleString();
  return String(v);
};
// percentage fields get a trailing % on their value
const isPctKey = (k) => /pct|percent/i.test(String(k));
// pull a value out of a row trying several possible key names
const pick = (obj, keys, fallback = "-") => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
      return fmtVal(obj[k]);
  }
  return fallback;
};

const ResultCard = ({ title, data }) => (
  <div className="col-md-6 mb-3">
    <div
      style={{
        border: "0.5px solid #f6efe9",
        borderRadius: 12,
        padding: 16,
      }}
    >
      <p style={{ color: "#8f4300", fontSize: 16, marginBottom: 12 }}>{title}</p>
      <table className="table" style={{ marginBottom: 0 }}>
        <tbody>
          {Object.entries(data).map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: "#703c19", border: "none", padding: "6px 0" }}>
                {fmtLabel(k)}
              </td>
              <td
                style={{
                  border: "none",
                  padding: "6px 0",
                  textAlign: "right",
                  fontWeight: 500,
                }}
              >
                {fmtVal(v)}
                {isPctKey(k) && fmtVal(v) !== "-" ? "%" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const FleetRow = ({ r, i }) => (
  <tr key={r.vehicle_id || i}>
    <td>{r.vehicle_name || r.vehicle_number || r.vehicle_id || "-"}</td>
    <td>{pick(r, ["acquisition_cost", "acquisition"])}</td>
    <td>{pick(r, ["operating_cost", "operating", "operating_total"])}</td>
    <td>{pick(r, ["depreciation", "cumulative_depreciation", "depreciation_total"])}</td>
    <td>{pick(r, ["residual_value", "residual"])}</td>
    <td>{pick(r, ["tco", "ownership_cost", "total_cost", "total_tco"])}</td>
    <td>{pick(r, ["cpk", "cost_per_km", "actual_cpk"])}</td>
  </tr>
);

const TcoCpk = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [config, setConfig] = useState(emptyConfig);
  const [saving, setSaving] = useState(false);
  const [tco, setTco] = useState(null);
  const [cpk, setCpk] = useState(null);
  const [computing, setComputing] = useState(false);

  const [fleet, setFleet] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  // JSON report (fleet-finance/tco/report)
  const [showReport, setShowReport] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    getFleetSummary();
    getVehicles();
  }, []);

  const getVehicles = () => {
    getWithAuthCall(ApiConfig.VEHICEL_AVALIVALITY_DROPDAWAN)
      .then((data) => {
        if (data?.data) setVehicles(data.data);
      })
      .catch((err) => console.log("vehicle dropdown err", err));
  };

  const changeConfig = (e) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  const loadConfig = () => {
    if (!config.vehicle_id) {
      notifyError("Select a vehicle first");
      return;
    }
    getWithAuthCall(ApiConfig.FLEET_FINANCE_TCO_CONFIG_SINGLE + config.vehicle_id)
      .then((data) => {
        const d = data?.data;
        if ((data?.success || data?.result) && d) {
          setConfig({
            vehicle_id: d.vehicle_id ?? config.vehicle_id,
            acquisition_cost: d.acquisition_cost ?? "",
            residual_value: d.residual_value ?? "",
            in_service_date: d.in_service_date
              ? String(d.in_service_date).slice(0, 10)
              : "",
            useful_life_years: d.useful_life_years ?? "",
            planned_cpk: d.planned_cpk ?? "",
            expected_km: d.expected_km ?? "",
            odometer_start: d.odometer_start ?? "",
            odometer_current: d.odometer_current ?? "",
          });
          notifySuccess("Config loaded");
        } else {
          notifyError(data?.message || "No config found for this vehicle");
        }
      })
      .catch((err) => {
        console.log("load config err", err);
        notifyError("Something went wrong");
      });
  };

  const saveConfig = (e) => {
    e.preventDefault();
    setSaving(true);
    const body = JSON.stringify({
      vehicle_id: Number(config.vehicle_id),
      acquisition_cost: Number(config.acquisition_cost) || 0,
      residual_value: Number(config.residual_value) || 0,
      in_service_date: config.in_service_date,
      useful_life_years: Number(config.useful_life_years) || 0,
      planned_cpk: Number(config.planned_cpk) || 0,
      expected_km: Number(config.expected_km) || 0,
      odometer_start: Number(config.odometer_start) || 0,
      odometer_current: Number(config.odometer_current) || 0,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_CONFIG_SAVE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "TCO config saved");
        } else {
          notifyError(data?.message || "Unable to save config");
        }
      })
      .catch((err) => {
        console.log("save config err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  const computeTco = () => {
    if (!config.vehicle_id) return notifyError("Select a vehicle first");
    setComputing(true);
    setTco(null);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_TCO_COMPUTE,
      JSON.stringify({ vehicle_id: Number(config.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) setTco(data?.data || {});
        else notifyError(data?.message || "Unable to compute TCO");
      })
      .catch((err) => {
        console.log("compute tco err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setComputing(false));
  };

  const computeCpk = () => {
    if (!config.vehicle_id) return notifyError("Select a vehicle first");
    setCpk(null);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_TCO_CPK,
      JSON.stringify({ vehicle_id: Number(config.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) setCpk(data?.data || {});
        else notifyError(data?.message || "Unable to compute CPK");
      })
      .catch((err) => {
        console.log("compute cpk err", err);
        notifyError("Something went wrong");
      });
  };

  const getFleetSummary = () => {
    setFleetLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_FLEET_SUMMARY, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data;
          setFleet(Array.isArray(d) ? d : d?.rows || d?.vehicles || []);
        } else {
          setFleet([]);
        }
      })
      .catch((err) => {
        console.log("fleet summary err", err);
        setFleet([]);
      })
      .finally(() => setFleetLoading(false));
  };

  const viewReport = () => {
    setShowReport(true);
    setReportLoading(true);
    setReport(null);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_REPORT, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result)
          setReport(data?.report || data?.data || data);
        else notifyError(data?.message || "Unable to load report");
      })
      .catch((err) => {
        console.log("tco report err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setReportLoading(false));
  };

  const downloadPdf = () => {
    setPdfLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_TCO_REPORT_PDF, JSON.stringify({}))
      .then((data) => {
        const filepath = data?.data?.filepath || data?.filepath;
        if ((data?.success || data?.result) && filepath) {
          window.open(ApiConfig.BASE_URL + filepath, "_blank");
        } else {
          notifyError(data?.message || "Unable to generate report");
        }
      })
      .catch((err) => {
        console.log("report pdf err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setPdfLoading(false));
  };

  const reportRows = report?.rows || report?.vehicles || [];

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_TCO">
        <div className="main-master-wrapper">
        <Tab.Container defaultActiveKey="config">
          <Nav variant="pills" className="td-nav" id="FleetFinanceTcoTabs">
            <Nav.Item className="td-tab">
              <Nav.Link className="td-link" eventKey="config">
                {t("TCO Configuration")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="td-tab">
              <Nav.Link className="td-link" eventKey="fleet">
                {t("Fleet Summary")}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Col sm={12} className="mt-3">
            <Tab.Content>
              {/* ===================== TAB 1: TCO Configuration ===================== */}
              <Tab.Pane eventKey="config">
                <div>
                  <div className="vehicleHead">
                    <p>{t("Vehicle TCO Configuration")}</p>
                  </div>
                  <Form onSubmit={saveConfig}>
                    <div className="row">
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Vehicle")} <span>&#42;</span>
                        </Form.Label>
                        <div className="d-flex">
                          <Form.Select
                            required
                            name="vehicle_id"
                            value={config.vehicle_id}
                            onChange={changeConfig}
                          >
                            <option value="">{t("Select Vehicle")}</option>
                            {vehicles.map((v) => (
                              <option key={v.vehicle_id} value={v.vehicle_id}>
                                {v.vehicle_number ||
                                  v.vehicle_name ||
                                  v.vehicle_id}
                              </option>
                            ))}
                          </Form.Select>
                          <button
                            type="button"
                            className="cx-btn-1"
                            style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                            onClick={loadConfig}
                          >
                            {t("Load")}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Acquisition Cost")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="acquisition_cost"
                          value={config.acquisition_cost}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Residual Value")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="residual_value"
                          value={config.residual_value}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("In-Service Date")}
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="in_service_date"
                          value={config.in_service_date}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Useful Life (years)")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="useful_life_years"
                          value={config.useful_life_years}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Planned CPK")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          name="planned_cpk"
                          value={config.planned_cpk}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Expected KM")}{" "}
                          <small style={{ opacity: 0.6 }}>
                            ({t("utilization benchmark")})
                          </small>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="expected_km"
                          value={config.expected_km}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Odometer Start")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="odometer_start"
                          value={config.odometer_start}
                          onChange={changeConfig}
                        />
                      </div>
                      <div className="col-md-3 form_input_main">
                        <Form.Label className="common-labels">
                          {t("Odometer Current")}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="odometer_current"
                          value={config.odometer_current}
                          onChange={changeConfig}
                        />
                      </div>
                    </div>
                    <div
                      className="d-flex flex-wrap"
                      style={{ gap: 12, marginTop: 12 }}
                    >
                      <button
                        className="cx-btn-2"
                        type="submit"
                        disabled={saving}
                      >
                        {saving ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Save Config")
                        )}
                      </button>
                      <button
                        type="button"
                        className="cx-btn-1"
                        onClick={computeTco}
                        disabled={computing}
                      >
                        {computing ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Compute TCO")
                        )}
                      </button>
                      <button
                        type="button"
                        className="cx-btn-1"
                        onClick={computeCpk}
                      >
                        {t("Compute CPK")}
                      </button>
                    </div>
                  </Form>

                  {(tco || cpk) && (
                    <div className="row" style={{ marginTop: 16 }}>
                      {tco && <ResultCard title={t("TCO Result")} data={tco} />}
                      {cpk && <ResultCard title={t("CPK Result")} data={cpk} />}
                    </div>
                  )}
                </div>
              </Tab.Pane>

              {/* ===================== TAB 2: Fleet Summary ===================== */}
              <Tab.Pane eventKey="fleet">
                <div>
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap"
                    style={{
                      borderBottom: "0.5px solid #f6efe9",
                      paddingBottom: 12,
                      marginBottom: 20,
                      gap: 12,
                    }}
                  >
                    <div className="vehicleHead">
                      <p
                        style={{
                          borderBottom: "none",
                          paddingBottom: 0,
                          marginBottom: 0,
                        }}
                      >
                        {t("Fleet TCO Summary")}
                      </p>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                      <button className="cx-btn-1" onClick={viewReport}>
                        {t("View Report")}
                      </button>
                      <button
                        className="cx-btn-2"
                        onClick={downloadPdf}
                        disabled={pdfLoading}
                      >
                        {pdfLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("Download TCO Report PDF")
                        )}
                      </button>
                    </div>
                  </div>
                  {fleetLoading ? (
                    <Loader />
                  ) : (
                    <div id="scroll_insideThe_Padding_tabel">
                      <div className="VA_table">
                        <table className="table tableAdmin">
                          <thead className="tableHead">
                            <tr>
                              <th>{t("Vehicle")}</th>
                              <th>{t("Acquisition")}</th>
                              <th>{t("Operating")}</th>
                              <th>{t("Depreciation")}</th>
                              <th>{t("Residual")}</th>
                              <th>{t("TCO")}</th>
                              <th>{t("CPK")}</th>
                            </tr>
                          </thead>
                          <tbody className="tableBody">
                            {fleet && fleet.length > 0 ? (
                              fleet.map((r, i) => <FleetRow r={r} i={i} key={r.vehicle_id || i} />)
                            ) : (
                              <tr>
                                <td colSpan={7}>
                                  <NoDataComp />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Tab.Container>
        </div>
      </div>

      {/* TCO JSON Report modal */}
      <Modal
        show={showReport}
        onHide={() => setShowReport(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{report?.title || t("Fleet TCO Report")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportLoading ? (
            <Loader />
          ) : report ? (
            <>
              <div className="row" style={{ marginBottom: 12 }}>
                <div className="col-md-6">
                  <b>{t("Currency")}:</b> {report.currency || "-"}
                </div>
                <div className="col-md-6">
                  <b>{t("Period")}:</b>{" "}
                  {(typeof report.period === "string" && report.period) ||
                    [
                      report.from_date || report.period?.from_date,
                      report.to_date || report.period?.to_date,
                    ]
                      .filter(Boolean)
                      .join(" - ") ||
                    "-"}
                </div>
              </div>
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Acquisition")}</th>
                      <th>{t("Operating")}</th>
                      <th>{t("Depreciation")}</th>
                      <th>{t("Residual")}</th>
                      <th>{t("TCO")}</th>
                      <th>{t("CPK")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {reportRows.length > 0 ? (
                      reportRows.map((r, i) => <FleetRow r={r} i={i} key={r.vehicle_id || i} />)
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {report.totals && (
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td>{t("Grand Total")}</td>
                        <td>{pick(report.totals, ["acquisition_cost", "acquisition"])}</td>
                        <td>{pick(report.totals, ["operating_cost", "operating"])}</td>
                        <td>{pick(report.totals, ["depreciation", "cumulative_depreciation"])}</td>
                        <td>{pick(report.totals, ["residual_value", "residual"])}</td>
                        <td>{pick(report.totals, ["tco", "ownership_cost", "total_cost"])}</td>
                        <td>{pick(report.totals, ["cpk", "cost_per_km"])}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

export default TcoCpk;
